<template>
  <div class="oi" dir="rtl">
    <table class="oi-table">
      <thead>
        <tr>
          <th class="oi-c-row">#</th>
          <th>{{ $t('documentType') }}</th>
          <th>{{ $t('trOrderItemLang') }}</th>
          <th>{{ $t('customerTitle') }}</th>
          <th class="oi-c-num">{{ $t('trOrderItemCounts') }}</th>
          <th>{{ $t('trOrderItemServices') }}</th>
          <th class="oi-c-num">{{ $t('totalCost') }}</th>
          <th class="oi-c-act" />
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(item, i) in items"
          :key="i"
          :class="{ 'oi-row--edit': editIndex === i }"
          @dblclick="view = { item: item, index: i }"
        >
          <td class="oi-c-row"><span class="oi-badge">{{ i + 1 }}</span></td>
          <td class="oi-strong">{{ nameOf(item.documentType) }}</td>
          <td>
            <span class="oi-lang">{{ nameOf(item.fromLanguage) }}</span>
            <v-icon size="14" class="oi-arrow">mdi-arrow-left</v-icon>
            <span class="oi-lang">{{ nameOf(item.toLanguage) }}</span>
          </td>
          <td class="oi-ellipsis" :title="item.customerTitle">{{ item.customerTitle }}</td>
          <td class="oi-c-num oi-num">
            {{ num(item.baseCount) }}<span v-if="num(item.addDocumentCount)" class="oi-muted"> + {{ num(item.addDocumentCount) }}</span>
          </td>
          <td>
            <div class="oi-tags">
              <span v-if="item.unofficial" class="oi-tag oi-tag--muted">{{ $t('unofficial') }}</span>
              <span v-for="s in activeServices(item)" :key="s" class="oi-tag">{{ $t(s) }}</span>
              <span v-if="!item.unofficial && !activeServices(item).length" class="oi-muted">—</span>
            </div>
          </td>
          <td class="oi-c-num oi-num oi-strong">{{ $numberFormat(num(item.totalCost)) }}</td>
          <td class="oi-c-act">
            <button type="button" class="oi-btn" :title="$t('show')" @click="view = { item: item, index: i }">
              <v-icon size="18">mdi-eye-outline</v-icon>
            </button>
            <button type="button" class="oi-btn oi-btn--primary" :title="$t('edit')" @click="$emit('edit', item, i)">
              <v-icon size="18">mdi-pencil-outline</v-icon>
            </button>
            <button type="button" class="oi-btn oi-btn--danger" :title="$t('remove')" @click="$emit('remove', i)">
              <v-icon size="18">mdi-trash-can-outline</v-icon>
            </button>
          </td>
        </tr>
      </tbody>
      <tfoot v-if="items.length > 1">
        <tr>
          <td colspan="6" class="oi-foot-label">{{ $t('total') }}</td>
          <td class="oi-c-num oi-num oi-strong">{{ $numberFormat(sum) }}</td>
          <td />
        </tr>
      </tfoot>
    </table>

    <!-- View dialog -->
    <v-dialog :value="!!view" max-width="640" scrollable :fullscreen="isMobile" @input="v => !v && (view = null)">
      <div v-if="view" class="oi-dlg" dir="rtl">
        <header class="oi-dlg__head">
          <span class="oi-badge oi-badge--lg">{{ view.index + 1 }}</span>
          <div class="oi-dlg__titles">
            <div class="oi-dlg__title">{{ nameOf(view.item.documentType) }}</div>
            <div class="oi-dlg__sub">{{ nameOf(view.item.fromLanguage) }} ← {{ nameOf(view.item.toLanguage) }}</div>
          </div>
          <button type="button" class="oi-btn" :title="$t('close')" @click="view = null">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </header>
        <div class="oi-dlg__body">
          <div class="oi-dlg__sec">{{ $t('trOrderSecDoc') }}</div>
          <dl class="oi-kv">
            <div><dt>{{ $t('customerTitle') }}</dt><dd>{{ view.item.customerTitle || '—' }}</dd></div>
            <div><dt>{{ $t('documentCount') }}</dt><dd class="oi-num">{{ view.item.documentCount || 0 }}</dd></div>
            <div><dt>{{ $t('baseCount') }}</dt><dd class="oi-num">{{ num(view.item.baseCount) }}</dd></div>
            <div><dt>{{ $t('addDocumentCount') }}</dt><dd class="oi-num">{{ num(view.item.addDocumentCount) }}</dd></div>
            <div><dt>{{ $t('countSpecialTranslat') }}</dt><dd class="oi-num">{{ num(view.item.countSpecialTranslat) }}</dd></div>
            <div><dt>{{ $t('percentDocumentCount') }}</dt><dd class="oi-num">{{ num(view.item.percentDocumentCount) }}%</dd></div>
            <div v-if="view.item.customertContent" class="oi-kv__wide"><dt>{{ $t('customerContent') }}</dt><dd>{{ view.item.customertContent }}</dd></div>
          </dl>

          <div class="oi-dlg__sec">{{ $t('trOrderSecFees') }}</div>
          <table class="oi-mini">
            <thead><tr><th /><th class="oi-c-num">{{ $t('trOrderCount') }}</th><th class="oi-c-num">{{ $t('trOrderUnit') }}</th><th class="oi-c-num">{{ $t('total') }}</th></tr></thead>
            <tbody>
              <tr><td>{{ $t('baseTranslate') }}</td><td class="oi-c-num oi-num">{{ num(view.item.baseCount) }}</td><td class="oi-c-num oi-num">{{ $numberFormat(num(view.item.basePrice)) }}</td><td class="oi-c-num oi-num">{{ $numberFormat(num(view.item.basePriceTotal)) }}</td></tr>
              <tr v-if="num(view.item.addDocumentPriceTotal)"><td>{{ $t('addDocument') }}</td><td class="oi-c-num oi-num">{{ num(view.item.addDocumentCount) }}</td><td /><td class="oi-c-num oi-num">{{ $numberFormat(num(view.item.addDocumentPriceTotal)) }}</td></tr>
              <tr v-if="num(view.item.specialTranslatTotal)"><td>{{ $t('specialTranslat') }}</td><td class="oi-c-num oi-num">{{ num(view.item.countSpecialTranslat) }}</td><td /><td class="oi-c-num oi-num">{{ $numberFormat(num(view.item.specialTranslatTotal)) }}</td></tr>
              <tr v-if="num(view.item.specialTranslatAddPriceTotal)"><td>{{ $t('specialTranslatAdditional') }}</td><td class="oi-c-num oi-num">{{ num(view.item.countSpecialTranslatAdditional) }}</td><td /><td class="oi-c-num oi-num">{{ $numberFormat(num(view.item.specialTranslatAddPriceTotal)) }}</td></tr>
              <tr v-for="s in activeServices(view.item)" :key="'v' + s">
                <td>{{ $t(s) }}</td>
                <td class="oi-c-num oi-num">{{ num(view.item[s + 'Count']) }}</td>
                <td class="oi-c-num oi-num">{{ $numberFormat(num(view.item[s + 'Price'])) }}</td>
                <td class="oi-c-num oi-num">{{ $numberFormat(num(view.item[s + 'Price']) * num(view.item[s + 'Count'])) }}</td>
              </tr>
              <tr v-if="num(view.item.urgentAmount)"><td>{{ $t('urgentAmount') }}</td><td /><td /><td class="oi-c-num oi-num">{{ $numberFormat(num(view.item.urgentAmount)) }}</td></tr>
              <tr v-if="num(view.item.discountTranslate) || num(view.item.discountService)" class="oi-neg">
                <td>{{ $t('discountTranslate') }} + {{ $t('discountService') }}</td><td /><td />
                <td class="oi-c-num oi-num">{{ $numberFormat(num(view.item.discountTranslate) + num(view.item.discountService)) }}</td>
              </tr>
            </tbody>
          </table>
          <div v-if="view.item.embassy && (view.item.embassyName || view.item.embassyCost)" class="oi-note">
            {{ $t('embassyName') }}: {{ view.item.embassyName || '—' }} · {{ $t('embassyCost') }}: {{ view.item.embassyCost || '—' }}
          </div>
          <div class="oi-grand">
            <span>{{ $t('totalCost') }}</span>
            <b class="oi-num">{{ $numberFormat(num(view.item.totalCost)) }}</b>
          </div>
        </div>
        <footer class="oi-dlg__foot">
          <v-btn color="primary" depressed class="app-form-action-btn" @click="editFromView">
            <v-icon small class="me-1">mdi-pencil-outline</v-icon>{{ $t('edit') }}
          </v-btn>
          <v-btn color="grey" outlined class="app-form-action-btn" @click="view = null">{{ $t('close') }}</v-btn>
        </footer>
      </div>
    </v-dialog>
  </div>
</template>

<script>
  var SERVICES = ['judicialApproval', 'foreignApproval', 'stampTranslator', 'officeServices', 'verificationServices', 'equalOriginal', 'inquiry', 'embassy']
  export default {
    name: 'TranslationOrderItem',
    props: {
      items: { type: Array, default: () => [] },
      editIndex: { type: Number, default: null },
    },
    data: () => ({
      view: null,
    }),
    computed: {
      sum () {
        var app = this
        return this.items.reduce(function (a, it) { return a + app.num(it.totalCost) }, 0)
      },
    },
    methods: {
      num (v) {
        var n = parseFloat(v)
        return isNaN(n) ? 0 : n
      },
      nameOf (o) {
        return o && o.name ? o.name : ''
      },
      activeServices (item) {
        return SERVICES.filter(function (k) { return item[k] === 'active' })
      },
      editFromView () {
        var v = this.view
        this.view = null
        this.$emit('edit', v.item, v.index)
      },
    },
  }
</script>

<style scoped>
.oi { width: 100%; overflow-x: auto; }
.oi-table { width: 100%; border-collapse: separate; border-spacing: 0; border: 1px solid rgba(15,23,42,.08); border-radius: 6px; overflow: hidden; font-size: .85rem; }
.oi-table th { background: #f6f8fb; font-weight: 600; font-size: .76rem; color: rgba(0,0,0,.6); text-align: start; padding: 9px 10px; white-space: nowrap; }
.oi-table td { padding: 8px 10px; border-top: 1px solid rgba(15,23,42,.06); vertical-align: middle; }
.oi-table tbody tr { cursor: default; transition: background .12s; }
.oi-table tbody tr:hover td { background: rgba(25,118,210,.04); }
.oi-row--edit td { background: rgba(245,124,0,.08) !important; }
.oi-table tfoot td { background: #fafbfd; border-top: 1px solid rgba(15,23,42,.1); }
.oi-foot-label { text-align: end; font-weight: 600; opacity: .7; }
.oi-c-row { width: 40px; text-align: center !important; }
.oi-c-num { text-align: end !important; white-space: nowrap; }
.oi-c-act { width: 110px; white-space: nowrap; text-align: end !important; }
.oi-num { direction: ltr; unicode-bidi: isolate; }
.oi-strong { font-weight: 600; }
.oi-muted { opacity: .55; }
.oi-ellipsis { max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.oi-badge { display: inline-grid; place-items: center; min-width: 22px; height: 22px; border-radius: 50%; background: rgba(25,118,210,.12); color: var(--v-primary-base); font-size: .74rem; font-weight: 700; }
.oi-badge--lg { min-width: 36px; height: 36px; font-size: .9rem; }
.oi-lang { white-space: nowrap; }
.oi-arrow { opacity: .45; margin: 0 2px; }
.oi-tags { display: flex; flex-wrap: wrap; gap: 4px; }
.oi-tag { font-size: .7rem; padding: 2px 7px; border-radius: 6px; background: rgba(25,118,210,.1); color: var(--v-primary-base); white-space: nowrap; }
.oi-tag--muted { background: rgba(0,0,0,.06); color: rgba(0,0,0,.7); }
.oi-btn { width: 30px; height: 30px; border-radius: 6px; display: inline-grid; place-items: center; color: rgba(0,0,0,.6); }
.oi-btn:hover { background: rgba(0,0,0,.06); }
.oi-btn--primary { color: var(--v-primary-base); }
.oi-btn--primary:hover { background: rgba(25,118,210,.1); }
.oi-btn--danger { color: #d81b60; }
.oi-btn--danger:hover { background: rgba(216,27,96,.1); }

.oi-dlg { background: #fff; border-radius: 6px; display: flex; flex-direction: column; max-height: 90vh; overflow: hidden; }
.oi-dlg__head { display: flex; align-items: center; gap: 12px; padding: 14px 18px; border-bottom: 1px solid rgba(15,23,42,.07); flex-shrink: 0; }
.oi-dlg__titles { flex: 1; min-width: 0; }
.oi-dlg__title { font-weight: 700; font-size: 1rem; }
.oi-dlg__sub { opacity: .6; font-size: .8rem; margin-top: 2px; }
.oi-dlg__body { flex: 1; min-height: 0; overflow-y: auto; padding: 16px 18px; }
.oi-dlg__sec { font-weight: 600; font-size: .84rem; color: var(--v-primary-base); margin: 4px 0 10px; }
.oi-dlg__foot { display: flex; justify-content: space-between; gap: 10px; padding: 12px 18px; background: #fafbfd; border-top: 1px solid rgba(15,23,42,.07); flex-shrink: 0; }
.oi-dlg__foot ::v-deep .v-btn { border-radius: 6px; }
.oi-kv { display: grid; grid-template-columns: 1fr 1fr; gap: 0 18px; margin: 0 0 18px; font-size: .85rem; }
.oi-kv > div { display: flex; justify-content: space-between; gap: 8px; padding: 7px 0; border-bottom: 1px dashed rgba(15,23,42,.08); }
.oi-kv__wide { grid-column: 1 / -1; flex-direction: column; }
.oi-kv dt { opacity: .65; }
.oi-kv dd { margin: 0; font-weight: 600; }
.oi-mini { width: 100%; border-collapse: collapse; font-size: .84rem; border: 1px solid rgba(15,23,42,.08); border-radius: 6px; }
.oi-mini th { background: #f6f8fb; font-size: .74rem; font-weight: 600; padding: 7px 10px; text-align: start; color: rgba(0,0,0,.6); }
.oi-mini td { padding: 7px 10px; border-top: 1px solid rgba(15,23,42,.06); }
.oi-neg td:last-child { color: #d81b60; }
.oi-note { font-size: .8rem; opacity: .75; margin-top: 10px; }
.oi-grand { display: flex; justify-content: space-between; align-items: baseline; margin-top: 14px; padding: 12px 14px; border-radius: 6px; background: rgba(25,118,210,.08); font-weight: 700; }
.oi-grand b { color: var(--v-primary-base); font-size: 1.05rem; }

.theme--dark .oi-table, .theme--dark .oi-mini { border-color: rgba(255,255,255,.1); }
.theme--dark .oi-table th, .theme--dark .oi-mini th { background: #262626; color: rgba(255,255,255,.7); }
.theme--dark .oi-table tfoot td, .theme--dark .oi-dlg__foot { background: #252525; }
.theme--dark .oi-dlg { background: #1e1e1e; }
.theme--dark .oi-btn { color: rgba(255,255,255,.7); }
</style>
